import { useEffect, useRef, useState } from 'react'
import { Command, Search, X } from 'lucide-react'
import { filterEditorCommands, type EditorCommand } from '../../utils/editorCommands.logic'

type EditorCommandPaletteProps = {
  commands: EditorCommand[]
  open: boolean
  onClose: () => void
  onExecute: (id: EditorCommand['id']) => void
}

export function EditorCommandPalette({ commands, open, onClose, onExecute }: EditorCommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const filtered = filterEditorCommands(commands, query)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActiveIndex(0)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    setActiveIndex(index => Math.min(index, Math.max(0, filtered.length - 1)))
  }, [filtered.length])

  if (!open) return null

  const executeActive = () => {
    const command = filtered[activeIndex]
    if (!command || !command.enabled) return
    onExecute(command.id)
    onClose()
  }

  return (
    <div className="editor-command-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <section
        className="editor-command-palette"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-command-title"
        onKeyDown={event => {
          if (event.key !== 'Tab') return
          const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)'))
          if (!focusable.length) return
          const current = focusable.indexOf(document.activeElement as HTMLElement)
          const next = event.shiftKey
            ? (current <= 0 ? focusable.length - 1 : current - 1)
            : (current === focusable.length - 1 ? 0 : current + 1)
          event.preventDefault()
          focusable[next].focus()
        }}
      >
        <div className="editor-command-heading">
          <span className="editor-command-title" id="editor-command-title"><Command size={15} /> Команды редактора</span>
          <button type="button" className="editor-icon-button" aria-label="Закрыть палитру команд" onClick={onClose}><X size={15} /></button>
        </div>
        <label className="editor-command-search">
          <Search size={15} aria-hidden="true" />
          <span className="sr-only">Поиск команды</span>
          <input
            ref={inputRef}
            value={query}
            onChange={event => { setQuery(event.target.value); setActiveIndex(0) }}
            onKeyDown={event => {
              if (event.key === 'Escape') { event.preventDefault(); onClose() }
              if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex(index => Math.min(index + 1, filtered.length - 1)) }
              if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex(index => Math.max(index - 1, 0)) }
              if (event.key === 'Enter') { event.preventDefault(); executeActive() }
            }}
            placeholder="Найти действие..."
            autoComplete="off"
          />
          <kbd>Esc</kbd>
        </label>
        <div className="editor-command-list" role="listbox" aria-label="Результаты поиска команд">
          {filtered.length === 0 && <p className="editor-command-empty">Команды не найдены</p>}
          {filtered.map((command, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              aria-disabled={!command.enabled}
              disabled={!command.enabled}
              className={`editor-command-item${index === activeIndex ? ' is-active' : ''}${command.dangerous ? ' is-dangerous' : ''}`}
              key={command.id}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => { onExecute(command.id); onClose() }}
            >
              <span><strong>{command.label}</strong><small>{command.description}</small></span>
              {command.shortcut && <kbd>{command.shortcut}</kbd>}
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}
